import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatMoney } from '../../../../utils/currency';
import { posProgress as posStepOf } from './splitPayments';

// Card payments through the POS go through several phases; showing them one after another makes
// the wait feel less like a frozen screen. `sent` is true once the provider accepted the sale.
// Seconds (once the sale was sent to the POS) before telling the cashier how to cancel.
const CANCEL_HINT_AFTER_SECONDS = 10;
// Getnet expires a sale the POS did not pick up after about this many seconds (observed in testing); the backend caps every provider.
const POS_PICKUP_TIMEOUT_SECONDS = 30;

const posMessage = (elapsed, sent, posProgress) => {
  // The POS already took the sale: from here on it is waiting for the card.
  if (posProgress === 'IN_PROGRESS' || posProgress === 'PROCESSED_UNCONFIRMED') {
    return { text: 'El POS está procesando el cobro...', hint: 'Seguí las indicaciones del equipo para pasar la tarjeta.' };
  }
  if (!sent) {
    return elapsed < 2
      ? { text: 'Estableciendo comunicación con el POS...' }
      : { text: 'Enviando los datos de la venta...' };
  }
  return elapsed < 4
    ? { text: 'Datos enviados. Esperando respuesta del POS...' }
    : {
        text: 'Esperando confirmación de pago...',
        hint: 'Si el POS no muestra el cobro, tocá su pantalla para iniciarlo.',
      };
};

// Estado de cada medio de pago en el resumen del cobro.
const ROW_STATE = {
  approved: { text: 'Cobrado', color: 'success.main', Icon: CheckCircleIcon },
  // Sin ícono: la pantalla ya tiene el indicador de carga grande; otro junto al monto es ruido.
  current: { text: 'Procesando...', color: 'primary.main', Icon: null },
  waiting: { text: 'Pendiente', color: 'text.secondary', Icon: ScheduleIcon },
  registered: { text: 'Se registra con la venta', color: 'text.secondary', Icon: ReceiptLongOutlinedIcon },
};

// Resumen de lo que se envió al POS: el monto y, si la venta se divide, cuál de los cobros es el que está en curso.
const PaymentSummary = ({ summary }) => {
  if (!summary?.rows?.length) return null;
  const { rows, total, ticketCurrency } = summary;
  const split = rows.length > 1;
  const { count, step } = posStepOf(rows);
  return (
    <Box
      data-testid="awaiting-summary"
      sx={{ mt: 3, mx: 'auto', maxWidth: 420, textAlign: 'left', border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}
    >
      {split && count > 0 && (
        <Typography variant="subtitle2" data-testid="awaiting-step" sx={{ mb: 1 }}>
          {count > 1 ? `Cobro ${step} de ${count} en el POS` : 'Cobro en el POS'}
        </Typography>
      )}
      {!split && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Enviado al POS</Typography>
      )}
      {rows.map((row) => {
        const { text, color, Icon } = ROW_STATE[row.state] || ROW_STATE.waiting;
        return (
          <Box key={row.uid} data-testid={`awaiting-row-${row.state}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.5 }}>
            <Typography variant="body2">{row.label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatMoney(row.amount, row.currency)}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150, justifyContent: 'flex-end', color }}>
                {Icon && <Icon sx={{ fontSize: 16 }} />}
                <Typography variant="caption" sx={{ color }}>{text}</Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
      {split && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Total de la venta</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatMoney(total, ticketCurrency)}</Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

const StepAwaitingPayment = ({ pos = false, sent = false, posProgress: posState = null, summary = null }) => {
  const posTookIt = posState === 'IN_PROGRESS' || posState === 'PROCESSED_UNCONFIRMED';
  const [elapsed, setElapsed] = useState(0);
  const sentAt = useRef(null);
  if (sent && sentAt.current === null) sentAt.current = elapsed;

  useEffect(() => {
    if (!pos) return undefined;
    setElapsed(0);
    const timer = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [pos]);

  const { text, hint } = pos
    ? posMessage(elapsed, sent, posState)
    : { text: 'Esperando confirmación de pago...' };

  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <CircularProgress size={50} sx={{ mb: 3 }} />
      <Typography variant="h6">{text}</Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {hint}
        </Typography>
      )}
      <PaymentSummary summary={summary} />
      {pos && sent && !posTookIt && POS_PICKUP_TIMEOUT_SECONDS - (elapsed - sentAt.current) > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          El cobro se cancela solo si el POS no lo toma en {POS_PICKUP_TIMEOUT_SECONDS - (elapsed - sentAt.current)} s
        </Typography>
      )}
      {pos && sent && elapsed >= CANCEL_HINT_AFTER_SECONDS && (
        <Alert severity="info" variant="outlined" sx={{ mt: 3, mx: 'auto', maxWidth: 440, textAlign: 'left' }}>
          Para cancelar el cobro, cancelá la operación desde el POS.
        </Alert>
      )}
    </Box>
  );
};

export default StepAwaitingPayment;
